/** Formules humidité — alignées backend/humidite_pipeline.py et scripts Standalone */

export const COEF_IMP = 0.15;
export const MASSE_COND_FACTEUR_A = 0.974;
export const MASSE_COND_FACTEUR_B = -4;

function roundMass(v) {
  if (v == null || !Number.isFinite(v)) return null;
  return Math.round(v * 100) / 100;
}

/** Repli : masse_cond = 0,974 × masse_banc − 4 (g) */
export function calcMasseConditionnementFromBanc(masseBanc) {
  const mb = parseFloat(masseBanc);
  if (!(mb > 0)) return null;
  return roundMass(MASSE_COND_FACTEUR_A * mb + MASSE_COND_FACTEUR_B);
}

/**
 * masse_cond_pli = masse_banc_pli × (M_cond_stack / M_banc_stack)
 * (aligné backend distribute_masses_conditionnement_plis_from_banc)
 */
export function distributeMassesConditionnementPlisFromBanc(massesBanc, masseStack) {
  const list = massesBanc || [];
  const n = list.length;
  if (!n) return [];

  const vals = list.map((m) => parseFloat(m));
  const stack = parseFloat(masseStack);

  if (vals.some((m) => !(m > 0)) || !(stack > 0)) {
    return vals.map(() => null);
  }

  const totalBanc = vals.reduce((a, b) => a + b, 0);
  if (!(totalBanc > 0)) {
    return vals.map(() => null);
  }
  const ratio = stack / totalBanc;
  return vals.map((v) => roundMass(v * ratio));
}

/** masse_cond_pli = M_banc_pli × (M_cond_stack / M_banc_stack) */
export function calcMasseCondPliProportionnelle(mBancPli, mCondStack, mBancStack) {
  const mb = parseFloat(mBancPli);
  const mc = parseFloat(mCondStack);
  const ms = parseFloat(mBancStack);
  if (!(mb > 0) || !(mc > 0) || !(ms > 0)) return null;
  return roundMass(mb * (mc / ms));
}

/** masse_séch_pli = M_imp_pli × (M_séch_stack / M_imp_stack) */
export function calcMasseSechPliProportionnelle(mImpPli, mSechStack, mImpStack) {
  const mi = parseFloat(mImpPli);
  const ms = parseFloat(mSechStack);
  const mist = parseFloat(mImpStack);
  if (!(mi > 0) || !(ms > 0) || !(mist > 0)) return null;
  return roundMass(mi * (ms / mist));
}

export function distributeMassesSechagePlisFromImp(massesImp, masseSechStack) {
  const list = massesImp || [];
  const vals = list.map((m) => parseFloat(m));
  const stack = parseFloat(masseSechStack);
  if (vals.some((m) => !(m > 0)) || !(stack > 0)) {
    return vals.map(() => null);
  }
  const totalImp = vals.reduce((a, b) => a + b, 0);
  if (!(totalImp > 0)) {
    return vals.map(() => null);
  }
  const ratio = stack / totalImp;
  return vals.map((v) => roundMass(v * ratio));
}

/**
 * @param {string[]} orderedPlyPns — ordre empilement
 * @param {Record<string, number>} masseBancByPn — clés normalisées (masse initiale banc)
 * @param {number} masseStack — M cond. stack (TT)
 * @returns {Record<string, number>} clé PN normalisée → masse cond. pli
 */
export function buildMasseCondPliMapFromBancAndStack(
  orderedPlyPns,
  masseBancByPn,
  masseStack
) {
  const norm = (s) =>
    String(s || "")
      .replace(/\s+/g, "")
      .replace(/-/g, "")
      .trim();

  const massesBanc = (orderedPlyPns || []).map((pn) => {
    const k = norm(pn);
    const raw = masseBancByPn?.[k] ?? masseBancByPn?.[pn];
    return parseFloat(raw);
  });

  const distributed = distributeMassesConditionnementPlisFromBanc(
    massesBanc,
    masseStack
  );

  const out = {};
  (orderedPlyPns || []).forEach((pn, i) => {
    const k = norm(pn);
    const m = distributed[i];
    if (k && m != null && Number.isFinite(m)) out[k] = m;
  });
  return out;
}

export function resolveMasseImpregnation(masseRaw) {
  if (Array.isArray(masseRaw)) {
    if (!masseRaw.length) return 0;
    return parseFloat(masseRaw[masseRaw.length - 1]) || 0;
  }
  return parseFloat(masseRaw) || 0;
}

/** M_anhydre = M_banc / (1 + H_Merlin/100) — aligné traitement_thermique_calc */
export function calcMasseAnhydre(masseBanc, hMerlinPct) {
  const mb = parseFloat(masseBanc);
  const hm = parseFloat(hMerlinPct);
  if (!(mb > 0) || !Number.isFinite(hm)) return null;
  const denom = 1 + hm / 100;
  if (!(denom > 0)) return null;
  return mb / denom;
}

/** H_cond_stack (%) = (M_cond − M_anhydre) / M_anhydre × 100 */
export function calcHumiditeCondStack(mCondStack, mBancStack, hMerlinStack) {
  const mc = parseFloat(mCondStack);
  const ma = calcMasseAnhydre(mBancStack, hMerlinStack);
  if (!(mc > 0) || !(ma > 0)) return null;
  return ((mc - ma) / ma) * 100;
}

/** H_cond_pli = H_merlin_pli × (H_cond_stack / H_merlin_stack) — alias propagateHumiditeCondParRatio */
export function calcHumiditeCondPli(hMerlinPli, hCondStack, hMerlinStack) {
  return propagateHumiditeCondParRatio(hMerlinPli, hCondStack, hMerlinStack);
}

/** Gain massique imprégnation (%) = (M_imp − M_anhydre) / M_anhydre × 100 */
export function calcGainMassiqueImpregPli(masseImp, masseAnhydre) {
  const mi = resolveMasseImpregnation(masseImp);
  const ma = parseFloat(masseAnhydre);
  if (!(mi > 0) || !(ma > 0)) return null;
  return ((mi - ma) / ma) * 100;
}

/** H_imp_pli = [(0,15×(M_imp−M_cond))+H_cond_pli] / M_anhydre × 100 */
export function calcHumiditeImpPli(masseImp, masseCond, masseAnhydre, hCondPli) {
  const mi = resolveMasseImpregnation(masseImp);
  const mc = parseFloat(masseCond);
  const ma = parseFloat(masseAnhydre);
  const hc = parseFloat(hCondPli);
  if (!(mi > 0) || !(mc > 0) || !(ma > 0) || !Number.isFinite(hc)) return null;
  return ((COEF_IMP * (mi - mc) + hc) / ma) * 100;
}

/** H_imp_stack = moyenne des H_imp_pli */
export function calcHumiditeImpStack(hImpPlis) {
  const vals = (hImpPlis || []).filter((v) => v != null && Number.isFinite(v));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * Masse conditionnement stack pour H séchage — aligné backend MasseConditionnementTotale.
 * Priorité : masse TT (conditionnement) puis somme des masses cond. plis (imprégnation).
 */
export function resolveMasseConditionnementStackRef(row) {
  if (!row) return null;
  const fromTt = parseFloat(row.masseConditionnementStack);
  if (Number.isFinite(fromTt) && fromTt > 0) return fromTt;

  const plis = row.masseConditionnementParPli || {};
  let sum = 0;
  let any = false;
  for (const v of Object.values(plis)) {
    let m;
    if (typeof v === "number") m = v;
    else if (v && typeof v === "object") {
      m = parseFloat(v.masseConditionnement ?? v.masse);
    } else m = parseFloat(v);
    if (Number.isFinite(m) && m > 0) {
      sum += m;
      any = true;
    }
  }
  return any ? sum : null;
}

/** H_séch_stack = H_imp_stack − ((M_imp − M_séch) / M_anhydre × 100) */
export function calcHumiditeSechageStack(
  hImpStack,
  masseImpStack,
  masseSechStack,
  masseAnhydreStack
) {
  const hi = parseFloat(hImpStack);
  const mi = parseFloat(masseImpStack);
  const ms = parseFloat(masseSechStack);
  const ma = parseFloat(masseAnhydreStack);
  if (!Number.isFinite(hi) || !(mi > 0) || !Number.isFinite(ms) || !(ma > 0)) {
    return null;
  }
  const h = hi - ((mi - ms) / ma) * 100;
  return Number.isFinite(h) ? h : null;
}

/** @deprecated H pli imprégnation = calcHumiditeImpPli ; conservé pour séchage / legacy */
export function propagateHumiditeImpParRatio(hRefPli, hImpStack, hDenomStack) {
  return propagateHumiditeCondParRatio(hRefPli, hImpStack, hDenomStack);
}

/** Répartition conditionnement : H_pli = H_merlin_pli × (H_cond_stack / H_merlin_moy_stack) */
export function propagateHumiditeCondParRatio(hMerlin, hCondStack, hBancMoyenne) {
  const hm = parseFloat(hMerlin);
  const hs = parseFloat(hCondStack);
  const hb = parseFloat(hBancMoyenne);
  if (!Number.isFinite(hm) || !Number.isFinite(hs) || !Number.isFinite(hb) || hb === 0) {
    return null;
  }
  return hm * (hs / hb);
}

/** H_séch_pli = H_imp_pli × (H_séch_stack / H_imp_stack) */
export function propagateHumiditeSechageParRatio(
  hSechStack,
  hImpByPosition,
  hImpStackOverride = null
) {
  const vals = Object.values(hImpByPosition).filter((v) => v != null && Number.isFinite(v));
  if (!vals.length || hSechStack == null || !Number.isFinite(hSechStack)) return {};
  const hImpStack =
    hImpStackOverride != null && Number.isFinite(parseFloat(hImpStackOverride))
      ? parseFloat(hImpStackOverride)
      : vals.reduce((a, b) => a + b, 0) / vals.length;
  if (hImpStack === 0) return {};
  const ratio = hSechStack / hImpStack;
  const out = {};
  for (const [pos, hImp] of Object.entries(hImpByPosition)) {
    out[pos] = hImp != null && Number.isFinite(hImp) ? hImp * ratio : null;
  }
  return out;
}

export function formatHumiditePct(v, digits = 4) {
  if (v == null || !Number.isFinite(v)) return null;
  return v.toFixed(digits);
}
